import { ASCENDING } from '../components/orders-grid/columns/constants';
import { defaultOrderBy as defaultCustomersOrderBy } from '../store/customers/grid/default-column-configuration';
import { defaultOrderBy as defaultFulfillmentOrderBy } from '../store/external-fulfillments/default-column-configuration';
import {
  defaultOrderBy as defaultOrdersOrderBy,
  defaultOrderByAsObject as defaultOrdersOrderByAsObject,
} from '../store/orders/grid/default-column-configuration';
import { defaultOrderBy as defaultReturnsOrderBy } from '../store/returns/grid/default-column-configuration';
import { defaultOrderBy as defaultShippingOrderBy } from '../store/shipments/grid/default-column-configuration';

const ordersGridContractOrderByParamMap = {
  age: 'Age',
  'allocation-status': 'Allocated',
  'amount-paid': 'AmountPaidValue',
  assignedTo: 'AssignedTo',
  'open-batch-name': 'Batch',
  'company-name': 'SellerBrandId',
  'buyer-id': 'BuyerUsername',
  buyer: 'Buyer',
  carrier: 'Carrier',
  'client-name': 'Store',
  company: 'Company',
  confirmation: 'ConfirmationType',
  country: 'Country',
  custom1: 'CustomField1',
  custom2: 'CustomField2',
  custom3: 'CustomField3',
  'deliver-by': 'DeliverByDateTime',
  'fulfillment-status': 'ExternalFulfillmentStatus',
  gift: 'IsGift',
  holdUntil: 'HoldUntilDateTime',
  'item-name': 'ItemName',
  'item-sku': 'ItemSku',
  'num-line-items': 'LineItemCount',
  'order-number': 'OrderNumber',
  'order-total': 'OrderTotal',
  orderDate: 'OrderDateTime',
  package: 'Package',
  paidDate: 'PaidDateTime',
  'picking-exception': 'PickBatchExceptionType',
  'pickup-status': 'PickupStatus',
  'pickup-time': 'PickupTime',
  quantity: 'Quantity',
  rate: 'Rate',
  recipient: 'Recipient',
  'requested-service': 'RequestedService',
  service: 'Service',
  // todo update this when SPD-8010 and SPD-8011 are complete
  serviceCollection: 'Service',
  'ship-by': 'ShipByDateTime',
  shipDate: 'ShipDateTime',
  shipFrom: 'ShipFrom',
  shippingAccount: 'ShippingAccount',
  shippingPaid: 'ShippingPaidValue',
  source: 'Source',
  state: 'State',
  status: 'Status',
  store: 'Store',
  tags: 'Tags',
  taxPaid: 'TaxPaidValue',
  weight: 'OrderWeight',
  zone: 'Zone',
  'warehouse-location': 'WarehouseLocation',
};

const allowedGroupByParams = ['Store', 'LineItemCount', 'ItemName', 'Quantity', 'ItemSku', 'RequestedService', 'Country'];

export const multipleLabelText = '(multiple)';

export const ordersGridContractOrderByParamReverseMap = {};
Object.keys(ordersGridContractOrderByParamMap).forEach((key) => {
  const value = ordersGridContractOrderByParamMap[key];
  ordersGridContractOrderByParamReverseMap[value] = key;
});

const shippingGridContractOrderByParamMap = {
  'amount-paid': 'AmountPaid',
  assignedTo: 'AssignedToId',
  carrier: 'CarrierId',
  carrierFee: 'CarrierFee',
  confirmation: 'ConfirmationServiceId',
  'created-date': 'CreatedDateTime',
  'customs-forms': 'HasCustomsForm',
  'delivery-notification': 'DeliveryNotification',
  'shipment-number': 'FulfillmentPlanId',
  insuranceFee: 'InsuranceFee',
  insurance: 'InsuranceProviderId',
  'packing-slip-printed': 'IsPackingSlipPrinted',
  'label-created': 'IsLabelCreated',
  'label-printed': 'IsLabelPrinted',
  'marketplace-notified': 'MarketplaceNotified',
  orderDate: 'OrderDateTime',
  'order-number': 'OrderNumber',
  package: 'PackageTypeId',
  paidDate: 'PaidDateTime',
  provider: 'ProviderId',
  quantity: 'Quantity',
  rate: 'Rate',
  service: 'ServiceId',
  // todo update this when SPD-8010 and SPD-8011 are complete
  serviceCollection: 'Service',
  'ship-date': 'ShipDateTime',
  shipFrom: 'ShipFromId',
  'shipment-email-notified': 'ShipmentNotification',
  shippingAccount: 'ShippingAccount',
  shippingPaid: 'ShippingPaid',
  city: 'ShipToCity',
  company: 'ShipToCompany',
  country: 'ShipToCountryCode',
  recipient: 'ShipToName',
  postalCode: 'ShipToPostalCode',
  state: 'ShipToState',
  source: 'Source',
  store: 'Store',
  taxPaid: 'TaxPaid',
  weight: 'Weight',
  'tracking-number': 'TrackingNumber',
  batchName: 'Batch',
};

const returnsGridContractOrderByParamMap = {
  carrier: 'CarrierId',
  confirmation: 'Confirmation',
  'created-date': 'CreatedDateTime',
  insuranceFee: 'InsuranceFeeValue',
  insurance: 'InsuranceProviderId',
  'label-printed': 'IsLabelPrinted',
  provider: 'LabelProviderId',
  'order-number': 'OrderNumber',
  package: 'PackageTypeId',
  rate: 'RateValue',
  'received-date': 'ReceivedDateTime',
  city: 'ReturnFromCity',
  country: 'ReturnFromCountryCode',
  'returning-party': 'ReturnFromName',
  state: 'ReturnFromState',
  'rma-number': 'RmaNumber',
  service: 'ServiceId',
  'ship-to': 'ShipFromId',
  'tracking-number': 'TrackingNumber',
};

const customersGridContractOrderByParamMap = {
  name: 'Name',
  city: 'City',
  countryCode: 'CountryCode',
  state: 'State',
  email: 'Email',
  company: 'Company',
  phone: 'Phone',
  tags: 'Tags',
  marketplaces: 'marketplaces',
  'marketplace-usernames': 'MarketplaceUsernames',
};

const fulfillmentsGridContractOrderByParamMap = {
  createdDate: 'CreatedDateTime',
  delegationDate: 'CreatedDateTime',
  shipDate: 'ShipDateTime',
  orderNumber: 'OrderNumber',
  batchName: 'Batch',
  tracking: 'TrackingNumber',
  provider: 'ProviderId',
  service: 'ServiceId',
  recipient: 'ShipToName',
  city: 'ShipToCity',
  state: 'ShipToState',
  country: 'ShipToCountryCode',
  marketplaceNotified: 'MarketplaceNotified',
  shipmentNotification: 'ShipmentNotification',
  carrier: 'CarrierId',
  orderDate: 'OrderDateTime',
  store: 'Store',
};

const gridTypeToColumnMap = {
  orders: ordersGridContractOrderByParamMap,
  shipping: shippingGridContractOrderByParamMap,
  returns: returnsGridContractOrderByParamMap,
  customers: customersGridContractOrderByParamMap,
  fulfillments: fulfillmentsGridContractOrderByParamMap,
};

class ColumnOrderByParamServiceClass {
  getOrderByParamName(gridType, orderByColumn) {
    return gridTypeToColumnMap[gridType][orderByColumn.id];
  }

  getOrdersGridOrderByParamsFromGroupByEnums(groupBys) {
    return groupBys.map((groupBy) => {
      const paramName = this.getOrderByParamName('orders', groupBy);
      return {
        orderBy: paramName,
        orderByDirection: groupBy.orderByDirection || ASCENDING,
      };
    });
  }

  getOrderBySortDirection(orderByColumn, asObject = false) {
    if (asObject) {
      return orderByColumn.sorted === 'ascending' ? 'Ascending' : 'Descending';
    }
    return orderByColumn.sorted === 'ascending' ? 'Asc' : 'Desc';
  }

  getOrderByApiParameter(gridType, orderByColumn, defaultOrderBy, asObject = false) {
    if (!orderByColumn) {
      return defaultOrderBy;
    }
    const paramName = this.getOrderByParamName(gridType, orderByColumn);
    const direction = this.getOrderBySortDirection(orderByColumn, asObject);

    if (!paramName) {
      return defaultOrderBy;
    }

    if (asObject) {
      return {
        orderBy: paramName,
        orderByDirection: direction,
      };
    }
    return `${paramName}${direction}`;
  }

  getOrdersGridOrderByApiParameterOrDefault(orderByColumn, asObject = false) {
    return this.getOrderByApiParameter('orders', orderByColumn, asObject ? defaultOrdersOrderByAsObject : defaultOrdersOrderBy, asObject);
  }

  getShippingGridOrderByApiParamOrDefault(orderByColumn, asObject = false) {
    return this.getOrderByApiParameter('shipping', orderByColumn, defaultShippingOrderBy, asObject);
  }

  getFulfillmentGridOrderByApiParamOrDefault(orderByColumn, asObject = false) {
    return this.getOrderByApiParameter('fulfillments', orderByColumn, defaultFulfillmentOrderBy, asObject);
  }

  getReturnsGridOrderByApiParamOrDefault(orderByColumn) {
    return this.getOrderByApiParameter('returns', orderByColumn, defaultReturnsOrderBy);
  }

  getCustomersGridOrderByApiParamOrDefault(orderByColumn) {
    const sortValue = this.getOrderByApiParameter('customers', orderByColumn, defaultCustomersOrderBy);
    const values = sortValue.match(/[A-Z][a-z]+/g); // Revamp this once BE handles things correctly
    return (
      values.length > 1 && {
        orderBy: values[0],
        orderDirection: values[1].toUpperCase(),
      }
    );
  }

  getFilteredGroupByParams(orderBys = []) {
    return orderBys.filter((param) => allowedGroupByParams.includes(param.orderBy));
  }
}

export const ColumnOrderByParamService = new ColumnOrderByParamServiceClass();
